// client/src/components/auth/Signup.js
import SocialAuthButtons from './SocialAuthButtons'
import InterestExpression from './InterestExpression'
import AccountCreatedModal from './AccountCreatedModal'
import useSignupForm from '../../hooks/useSignupForm'
import { Link } from 'react-router-dom'
import './Auth.css'

function Signup() {
  const {
    // Form state
    userData,
    password,
    confirmPassword,
    passwordsMatch,
    creating,
    agreedToTerms,
    showPassword,
    showConfirmPassword,
    passwordStrength,
    termsCheckboxRef,
    showSuccessModal,
    errors,

    // Referral code state
    referralCode,
    isValidatingReferralCode,
    referralCodeStatus,

    // Refs for autofill detection
    emailRef,
    inviteCodeRef,
    passwordRef,
    confirmPasswordRef,
    referralCodeRef,

    // Form handlers
    handleInputChange,
    handlePasswordChange,
    handleConfirmPasswordChange,
    handleTermsChange,
    handleSubmit,
    togglePasswordVisibility,
    toggleConfirmPasswordVisibility,
    handleReferralCodeChange,

    // Social auth handlers
    handleSocialAuthSuccess,
    handleSocialAuthError,

    // Navigation handlers
    navigateToSignIn,
    handleSuccessModalContinue,
  } = useSignupForm()

  return (
    <div className="auth-container">
      <AccountCreatedModal
        show={showSuccessModal}
        email={userData.email}
        onContinue={handleSuccessModalContinue}
      />

      <div className="auth-card">
        <h2 className="auth-title">Join Serve Sync</h2>

        <SocialAuthButtons
          onSuccess={handleSocialAuthSuccess}
          onError={handleSocialAuthError}
          disabled={creating}
        />

        <form onSubmit={handleSubmit} className="auth-form">
          {/* Live region for screen readers to announce validation errors */}
          <div
            aria-live="polite"
            aria-atomic="true"
            className="sr-only"
            role="status"
          >
            {Object.keys(errors).length > 0 &&
              `Form has ${Object.keys(errors).length} error${
                Object.keys(errors).length > 1 ? 's' : ''
              }. Please review and correct.`}
          </div>

          <div className="form-group">
            <label className="form-label">
              Email Address <span className="required-asterisk">*</span>
            </label>
            <input
              ref={emailRef}
              name="email"
              type="email"
              value={userData.email}
              onChange={handleInputChange}
              className={`form-input ${errors.email ? 'is-invalid' : ''}`}
              required
              disabled={creating}
              aria-invalid={errors.email ? 'true' : 'false'}
              aria-describedby={errors.email ? 'email-error' : undefined}
            />
            {errors.email && (
              <div id="email-error" className="invalid-feedback" role="alert">
                {errors.email}
              </div>
            )}
          </div>

          <div className="form-group password-input-group">
            <label htmlFor="password-input" className="form-label">
              Password <span className="required-asterisk">*</span>
            </label>
            <div className="password-input-wrapper">
              <input
                ref={passwordRef}
                id="password-input"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={handlePasswordChange}
                className={`form-input ${errors.password ? 'is-invalid' : ''}`}
                required
                disabled={creating}
                aria-invalid={errors.password ? 'true' : 'false'}
                aria-describedby={
                  errors.password ? 'password-error' : undefined
                }
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={togglePasswordVisibility}
                disabled={creating}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
            {errors.password && (
              <div
                id="password-error"
                className="invalid-feedback"
                role="alert"
              >
                {errors.password}
              </div>
            )}
            {password && (
              <div
                className="password-requirements"
                role="status"
                aria-live="polite"
              >
                <div className="password-requirement-title">
                  Password must contain:
                </div>
                <ul className="password-requirement-list">
                  <li
                    className={`password-requirement ${
                      passwordStrength.hasMinLength ? 'met' : ''
                    }`}
                  >
                    {passwordStrength.hasMinLength ? '✓' : '○'} At least 8
                    characters
                  </li>
                  <li
                    className={`password-requirement ${
                      passwordStrength.hasUpperCase ? 'met' : ''
                    }`}
                  >
                    {passwordStrength.hasUpperCase ? '✓' : '○'} One uppercase
                    letter
                  </li>
                  <li
                    className={`password-requirement ${
                      passwordStrength.hasLowerCase ? 'met' : ''
                    }`}
                  >
                    {passwordStrength.hasLowerCase ? '✓' : '○'} One lowercase
                    letter
                  </li>
                  <li
                    className={`password-requirement ${
                      passwordStrength.hasNumber ? 'met' : ''
                    }`}
                  >
                    {passwordStrength.hasNumber ? '✓' : '○'} One number
                  </li>
                </ul>
              </div>
            )}
          </div>

          {passwordStrength.isValid && (
            <div className="form-group password-input-group">
              <label htmlFor="confirm-password-input" className="form-label">
                Confirm Password <span className="required-asterisk">*</span>
              </label>
              <div className="password-input-wrapper">
                <input
                  ref={confirmPasswordRef}
                  id="confirm-password-input"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={handleConfirmPasswordChange}
                  className={`form-input ${errors.confirmPassword ? 'is-invalid' : ''}`}
                  required
                  disabled={creating}
                  aria-invalid={errors.confirmPassword ? 'true' : 'false'}
                  aria-describedby={
                    errors.confirmPassword
                      ? 'confirm-password-error'
                      : undefined
                  }
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={toggleConfirmPasswordVisibility}
                  disabled={creating}
                  aria-label={
                    showConfirmPassword ? 'Hide password' : 'Show password'
                  }
                >
                  {showConfirmPassword ? 'Hide' : 'Show'}
                </button>
              </div>
              {errors.confirmPassword && (
                <div
                  id="confirm-password-error"
                  className="invalid-feedback"
                  role="alert"
                >
                  {errors.confirmPassword}
                </div>
              )}
              {!errors.confirmPassword && passwordsMatch === true && (
                <div className="password-match-indicator password-match-success">
                  Passwords match ✓
                </div>
              )}
            </div>
          )}

          <div className="form-group">
            <label className="form-label">
              Invite Code <span className="required-asterisk">*</span>
            </label>
            <input
              ref={inviteCodeRef}
              name="invite_code"
              type="text"
              value={userData.invite_code}
              onChange={handleInputChange}
              className={`form-input ${errors.invite_code ? 'is-invalid' : ''}`}
              required
              disabled={creating}
              aria-invalid={errors.invite_code ? 'true' : 'false'}
              aria-describedby={
                errors.invite_code ? 'invite-code-error' : undefined
              }
            />
            {errors.invite_code && (
              <div
                id="invite-code-error"
                className="invalid-feedback"
                role="alert"
              >
                {errors.invite_code}
              </div>
            )}
            <InterestExpression />
          </div>

          <div className="form-group">
            <label className="form-label">Referral Code (Optional)</label>
            <div style={{ position: 'relative' }}>
              <input
                ref={referralCodeRef}
                name="referralCode"
                type="text"
                value={referralCode}
                onChange={handleReferralCodeChange}
                className={`form-input ${errors.referralCode ? 'is-invalid' : ''} ${
                  referralCodeStatus === 'valid' ? 'is-valid' : ''
                }`}
                disabled={creating}
                placeholder="Have a referral code? Enter it here!"
                aria-invalid={errors.referralCode ? 'true' : 'false'}
                aria-describedby={
                  errors.referralCode ? 'referral-code-error' : undefined
                }
              />
              {isValidatingReferralCode && (
                <div
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                  }}
                >
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-label="Validating"
                  />
                </div>
              )}
              {!isValidatingReferralCode && referralCodeStatus === 'valid' && (
                <div
                  role="status"
                  aria-live="polite"
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'var(--fl-color-success)',
                    fontSize: '1.2rem',
                  }}
                  aria-label="Valid referral code"
                >
                  ✓
                </div>
              )}
              {!isValidatingReferralCode &&
                referralCodeStatus === 'invalid' && (
                  <div
                    role="status"
                    aria-live="polite"
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: 'var(--fl-color-error)',
                      fontSize: '1.2rem',
                    }}
                    aria-label="Invalid referral code"
                  >
                    ✗
                  </div>
                )}
            </div>
            {errors.referralCode && (
              <div
                id="referral-code-error"
                className="invalid-feedback"
                role="alert"
                style={{ display: 'block' }}
              >
                {errors.referralCode}
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="phone-e164-input" className="form-label">
              Phone Number (Optional)
            </label>
            <input
              id="phone-e164-input"
              name="phone_e164"
              type="tel"
              value={userData.phone_e164}
              onChange={handleInputChange}
              className={`form-input ${errors.phone_e164 ? 'is-invalid' : ''}`}
              disabled={creating}
              placeholder="+15551234567"
              aria-invalid={errors.phone_e164 ? 'true' : 'false'}
              aria-describedby={
                errors.phone_e164 ? 'phone-e164-error' : undefined
              }
            />
            {errors.phone_e164 && (
              <div
                id="phone-e164-error"
                className="invalid-feedback"
                role="alert"
              >
                {errors.phone_e164}
              </div>
            )}
          </div>

          <div
            className={`form-group checkbox-group ${creating ? 'disabled' : ''}`}
          >
            <label htmlFor="sms-consent-checkbox">
              <input
                type="checkbox"
                id="sms-consent-checkbox"
                name="sms_consent_status"
                checked={Boolean(userData.sms_consent_status)}
                onChange={handleInputChange}
                disabled={creating}
              />
              <span className="form-label-inline">
                Sign me up for SMS notifications from ServeSync. Msg frequency
                varies. Msg &amp; data rates may apply. Reply HELP for help,
                STOP to opt out.{' '}
                <Link
                  to="/sms-notifications-disclosure"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="legal-link"
                  onClick={(e) => e.stopPropagation()}
                >
                  SMS Policy
                </Link>
              </span>
            </label>
            {errors.sms_consent_status && (
              <div
                className="invalid-feedback"
                role="alert"
                style={{ display: 'block' }}
              >
                {errors.sms_consent_status}
              </div>
            )}
          </div>

          <div
            className={`form-group checkbox-group terms-checkbox ${
              errors.terms ? 'error' : ''
            } ${creating ? 'disabled' : ''}`}
          >
            <label htmlFor="terms-checkbox">
              <input
                type="checkbox"
                id="terms-checkbox"
                ref={termsCheckboxRef}
                checked={agreedToTerms}
                onChange={handleTermsChange}
                disabled={creating}
                required
                aria-invalid={errors.terms ? 'true' : 'false'}
                aria-describedby={errors.terms ? 'terms-error' : undefined}
              />
              <span className="form-label-inline">
                I agree to the{' '}
                <Link
                  to="/terms-of-service"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="legal-link"
                  onClick={(e) => e.stopPropagation()}
                >
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link
                  to="/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="legal-link"
                  onClick={(e) => e.stopPropagation()}
                >
                  Privacy Policy
                </Link>
              </span>
            </label>
            {errors.terms && (
              <div
                id="terms-error"
                className="form-error-message"
                role="alert"
                aria-live="polite"
                style={{ marginTop: '0.5rem' }}
              >
                {errors.terms}
              </div>
            )}
          </div>

          <button
            type="submit"
            className={`auth-submit-btn ${creating ? 'loading' : ''}`}
            disabled={
              creating ||
              !userData.email ||
              !userData.invite_code ||
              !passwordStrength.isValid ||
              !passwordsMatch ||
              !agreedToTerms
            }
          >
            {creating ? 'Creating Account...' : 'Create Account'}
          </button>
        </form>

        <div className="auth-links">
          <button
            className="auth-link-btn"
            onClick={navigateToSignIn}
            disabled={creating}
          >
            Already have an account? Sign in →
          </button>
        </div>
      </div>
    </div>
  )
}

export default Signup
